/**
 * HeaderContext and Provider
 *
 * @fileoverview
 * Context and Provider to manage header design state.
 * Provides header state to all header-related components:
 * - Header component
 * - AboveHeader (Promo Banner)
 * - MegaMenu
 * - Logo component
 * - Search modal
 *
 * @see task-sticky-header.md for requirements
 */

import React, {createContext, useContext, useState, useMemo, useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';

import {useHeaderState} from '@amblique/king-living/app/hooks/use-header-state';
import {
  HEADER_DESIGN_STATE,
  HEADER_STYLE,
  STICKY_TOP_ALWAYS_VISIBLE_PX,
  DEFAULT_HEADER_STATE,
} from '@amblique/king-living/app/constants/header';
import {syncPdpHideOnScrollProgrammaticScroll} from '@amblique/king-living/app/utils/utils';
import {SEARCH_TAB_ID} from '@amblique/king-living/app/constants/navigation-drawer';

/**
 * Default context value
 */
const defaultContextValue = {
  headerState: DEFAULT_HEADER_STATE,
  headerStyle: HEADER_STYLE.LIGHT,
  originalState: DEFAULT_HEADER_STATE,
  isSticky: false,
  scrollY: 0,
  forcesSolid: false,
  stickyDisabled: false,
  isHeaderHidden: false,
  isMegaMenuOpen: false,
  isSearchOpen: false,
  isHeaderHovered: false,
  navDrawerTabId: null,
  selectedCategoryId: null,
  setMegaMenuOpen: () => {},
  setSearchOpen: () => {},
  setHeaderHovered: () => {},
  setHideOnScroll: () => {},
  openNavDrawer: () => {},
  closeNavDrawer: () => {},
  setSelectedCategoryId: () => {},
};

/**
 * HeaderContext
 */
const HeaderContext = createContext(defaultContextValue);

/**
 * HeaderProvider
 *
 * Provides header state to all header-related components.
 * Handles overrides when mega menu or search is open.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} [props.configuredState='solid'] - Configured header state from Contentful
 * @returns {React.ReactElement}
 *
 * @example
 * <HeaderProvider configuredState="transparent">
 *   <Header />
 * </HeaderProvider>
 */
export function HeaderProvider({children, configuredState = DEFAULT_HEADER_STATE}) {
  const location = useLocation();
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [hideOnScroll, setHideOnScrollState] = useState(true);
  const [navDrawerTabId, setNavDrawerTabId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isHeaderFocused, setIsHeaderFocused] = useState(false);
  const desktopMqlRef = useRef(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 992px)') : null
  );
  const [isDesktop, setIsDesktop] = useState(() => desktopMqlRef.current?.matches ?? false);

  useEffect(() => {
    const mql = desktopMqlRef.current;

    if (!mql) {
      return undefined;
    }

    const onChange = () => setIsDesktop(mql.matches);

    onChange();
    mql.addEventListener('change', onChange);

    return () => mql.removeEventListener('change', onChange);
  }, []);

  const {
    headerState: baseState,
    headerStyle: baseStyle,
    originalState,
    isSticky,
    scrollY,
    scrollDirection,
    forcesSolid,
    stickyDisabled,
  } = useHeaderState(configuredState);

  // Wrapper functions for setting state
  const setMegaMenuOpen = useCallback((isOpen) => {
    setIsMegaMenuOpen(isOpen);
  }, []);

  const setSearchOpen = useCallback((isOpen) => {
    setIsSearchOpen(isOpen);
  }, []);

  const setHeaderHovered = useCallback((isHovered) => {
    setIsHeaderHovered(isHovered);
  }, []);

  const closeNavDrawer = useCallback(() => {
    setNavDrawerTabId(null);
    setSelectedCategoryId(null);
    setIsMegaMenuOpen(false);
    setIsSearchOpen(false);
  }, []);

  useEffect(() => {
    closeNavDrawer();
  }, [location.pathname, location.search, closeNavDrawer]);

  const openNavDrawer = useCallback((tabId) => {
    if (!tabId) {
      closeNavDrawer();

      return;
    }

    setNavDrawerTabId((current) => {
      if (current === tabId) {
        setSelectedCategoryId(null);
        setIsMegaMenuOpen(false);
        setIsSearchOpen(false);

        return null;
      }

      setSelectedCategoryId(null);
      setIsSearchOpen(tabId === SEARCH_TAB_ID);
      setIsMegaMenuOpen(tabId !== SEARCH_TAB_ID);

      return tabId;
    });
  }, [closeNavDrawer]);

  // Override to solid when mega menu or search is open.
  // Gradient hover stays inverted (white on glass) — do not flip to linen.
  const headerState = useMemo(() => {
    if (isMegaMenuOpen || isSearchOpen) return HEADER_DESIGN_STATE.SOLID;

    return baseState;
  }, [isMegaMenuOpen, isSearchOpen, baseState]);

  // Override style to light when mega menu or search is open.
  // Gradient hover keeps dark (white) chrome.
  const headerStyle = useMemo(() => {
    if (isMegaMenuOpen || isSearchOpen) return HEADER_STYLE.LIGHT;

    return baseStyle;
  }, [isMegaMenuOpen, isSearchOpen, baseStyle]);

  // Hide on scroll down, show on scroll up (4px). Stay visible in the top 68px,
  // when mega menu / search / keyboard focus is in the header, and ignore iOS overscroll.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const onFocusIn = (event) => {
      const header = document.querySelector('[data-header-wrapper]');

      setIsHeaderFocused(Boolean(header && event.target instanceof Node && header.contains(event.target)));
    };

    document.addEventListener('focusin', onFocusIn);

    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  const isHeaderHidden = useMemo(() => {
    if (!isDesktop || scrollY < 0) {
      return false;
    }

    const overlaysClosed = !isMegaMenuOpen && !isSearchOpen;
    const canHide = hideOnScroll && !stickyDisabled && overlaysClosed && !isHeaderFocused;

    if (!canHide || scrollY <= STICKY_TOP_ALWAYS_VISIBLE_PX) {
      return false;
    }

    return scrollDirection === 'down';
  }, [
    hideOnScroll,
    isDesktop,
    isHeaderFocused,
    isMegaMenuOpen,
    isSearchOpen,
    scrollDirection,
    scrollY,
    stickyDisabled,
  ]);

  useEffect(() => {
    syncPdpHideOnScrollProgrammaticScroll(hideOnScroll);
  }, [hideOnScroll]);

  const setHideOnScroll = useCallback((enabled) => {
    setHideOnScrollState(enabled);
  }, []);

  const value = useMemo(
    () => ({
      headerState,
      headerStyle,
      originalState,
      isSticky,
      scrollY,
      forcesSolid,
      stickyDisabled,
      isHeaderHidden,
      isMegaMenuOpen,
      isSearchOpen,
      isHeaderHovered,
      navDrawerTabId,
      selectedCategoryId,
      setMegaMenuOpen,
      setSearchOpen,
      setHeaderHovered,
      setHideOnScroll,
      openNavDrawer,
      closeNavDrawer,
      setSelectedCategoryId,
    }),
    [
      headerState,
      headerStyle,
      originalState,
      isSticky,
      scrollY,
      forcesSolid,
      stickyDisabled,
      isHeaderHidden,
      isMegaMenuOpen,
      isSearchOpen,
      isHeaderHovered,
      navDrawerTabId,
      selectedCategoryId,
      setMegaMenuOpen,
      setSearchOpen,
      setHeaderHovered,
      setHideOnScroll,
      openNavDrawer,
      closeNavDrawer,
      setSelectedCategoryId
    ]
  );

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
}

HeaderProvider.propTypes = {
  children: PropTypes.node.isRequired,
  configuredState: PropTypes.oneOf([
    HEADER_DESIGN_STATE.TRANSPARENT,
    HEADER_DESIGN_STATE.GRADIENT,
    HEADER_DESIGN_STATE.SOLID
  ]),
};

/**
 * useHeaderContext Hook
 *
 * Access header state from any component within HeaderProvider.
 *
 * @returns {Object} Header context value
 * @throws {Error} If used outside HeaderProvider
 *
 * @example
 * // Basic usage
 * const {headerState, headerStyle, isSticky} = useHeaderContext();
 *
 * @example
 * // Control mega menu state
 * const {setMegaMenuOpen} = useHeaderContext();
 * setMegaMenuOpen(true);
 *
 * @example
 * // Check if overlays are open
 * const {isMegaMenuOpen, isSearchOpen} = useHeaderContext();
 */
export function useHeaderContext() {
  const context = useContext(HeaderContext);

  if (context === null) {
    throw new Error('useHeaderContext must be used within HeaderProvider');
  }

  return context;
}

/**
 * Whether a click/key target inside `[data-flyout-frozen]` should remain interactive
 * (e.g. optional add-on checkboxes below the customize block).
 *
 * @param {EventTarget|null} target
 * @returns {boolean}
 */
export function isFlyoutFrozenInteractiveTarget(target) {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest([
    'button',
    'input',
    'select',
    'textarea',
    'a[href]',
    'label',
    '[role="button"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="switch"]',
    '[role="link"]',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')));
}

/**
 * Props for PDP desktop flyout `.variations-section` — blocks child interaction and closes flyout on click.
 * Uses capture phase + global `[data-flyout-frozen] * { pointer-events: none }` styles.
 * Interactive descendants (checkboxes, links, buttons) stay usable.
 * @param {Function} onBlockedInteract
 * @returns {Object}
 */
export function buildDesktopPdpFlyoutFrozenProps(onBlockedInteract) {
  if (typeof onBlockedInteract !== 'function') {
    return {};
  }

  return {
    'data-flyout-frozen': 'true',
    'onClickCapture': (event) => {
      if (isFlyoutFrozenInteractiveTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onBlockedInteract();
    },
  };
}

export default HeaderContext;
